import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TablePagination as MuiTablePagination,
  Tooltip as MuiTooltip,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Loader } from '@alumni-ventures-group/react-shared/lib/components';
import StyledSelect from '../common/StyledSelect';
import useAvailableFundOfferings from '../common/useAvailableFundOfferings';
import {
  FundsTableContainer,
  FundsTableHeader,
  FundsTableTitle,
  FiltersContainer,
  FilterWrapper,
  FilterLabel,
  StyledTableHeaderCell,
  StyledTableRow,
  StyledTableCell,
  FundTypePill,
  ClickableLink,
} from './FundsTableStyles';
import {
  formatDateTable,
  formatFoundationVintage,
  sortData,
  filterData,
  getUniqueFundTypes,
  getUniqueFundFamilies,
  mapFundTypeToFullName,
} from './helpers';
import isFundOpenThroughFinalClose from './fundAvailability';

const FundsTable = () => {
  const { availableFundsForPortal, isLoading, isError } = useAvailableFundOfferings();
  const [fundTypeFilter, setFundTypeFilter] = useState('All Types');
  const [fundFamilyFilter, setFundFamilyFilter] = useState('All Funds');
  const [sortField, setSortField] = useState('finalClose');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isFundTypeModalOpen, setIsFundTypeModalOpen] = useState(false);

  const fundsData = useMemo(() => {
    if (!availableFundsForPortal || !Array.isArray(availableFundsForPortal)) {
      return [];
    }
    
    const now = new Date();
    
    const mapped = availableFundsForPortal.map((fund) => ({
      fundFamilyName: fund.fundFamilyName,
      fundVintage: fund.findVintageNumber?.toString(),
      type: mapFundTypeToFullName(fund.fundType),
      fundOpens: fund.initialCloseDate,
      firstClose: fund.firstClose,
      secondClose: fund.secondClose,
      finalClose: fund.finalCloseDate,
      isOpen: isFundOpenThroughFinalClose(fund.finalCloseDate, now),
      qcpLink: fund.url,
    }));
    return mapped;
  }, [availableFundsForPortal]);

  const fundTypeOptions = useMemo(() => {
    const types = getUniqueFundTypes(fundsData);
    return [
      { label: 'All Types', value: 'All Types' },
      ...types.map((type) => ({ label: type, value: type })),
    ];
  }, [fundsData]);

  const fundFamilyOptions = useMemo(() => {
    const families = getUniqueFundFamilies(fundsData);
    return [
      { label: 'All Funds', value: 'All Funds' },
      ...families.map((family) => ({ label: family, value: family })),
    ];
  }, [fundsData]);

  const filteredAndSortedData = useMemo(() => {
    const data = filterData(fundsData, fundTypeFilter, fundFamilyFilter);
    return sortData(data, sortField, sortOrder);
  }, [fundsData, fundTypeFilter, fundFamilyFilter, sortField, sortOrder]);

  const visibleRows = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredAndSortedData.slice(start, end);
  }, [filteredAndSortedData, page, rowsPerPage]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setPage(0);
  };

  const handleFundTypeChange = (value) => {
    setFundTypeFilter(value);
    setPage(0);
  };

  const handleFundFamilyChange = (value) => {
    setFundFamilyFilter(value);
    setPage(0);
  };


  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    if (field === 'finalClose' && sortOrder === 'asc') {
      return null;
    }
    return sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
  };

  const tooltipContent = (
    <Box>
      Signing on or prior to the First Close date earns our highest fee deductions. See our{' '}
      <a
        href="https://3925488.fs1.hubspotusercontent-na1.net/hubfs/3925488/_SoT_Mirror/01_Policies_and_Products/AV_Loyalty_Rewards_Performance_and_Fees.pdf"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#7b569b', textDecoration: 'underline' }}
      >
        Loyalty Rewards, Performance, and Fees brochure
      </a>
      {' '}for more info.
    </Box>
  );

  const fundTypeModalParagraphs = [
    'Every investor has their own goals, strategies, and approaches. '
      + 'We\'ve designed our venture funds to allow for various portfolio construction approaches.',
  ];
  const fundTypeModalBullets = [
    {
      key: 'core',
      title: 'Our Core Funds',
      description: ' are our most diversified funds - creating portfolios of 20-30 companies '
        + 'diversified by stage, sector, geography, and lead investor. These include our Alumni '
        + 'Funds and Foundation Funds.',
    },
    {
      key: 'core-holding',
      description: 'For many investors, this is the core holding they build upon every year.',
    },
    {
      key: 'focused',
      title: 'Our Focused Funds',
      description: ' are for those with a strong interest in a vertical segment such as HealthTech, '
        + 'Al and Robotics, Deep Tech, etc.',
    },
    {
      key: 'syndication',
      title: 'Our Syndication Funds',
      description: ' are designed to supplement the rest of your portfolio. These are deals where '
        + 'we provide you with all diligence materials and you decide whether to invest alongside '
        + 'our funds in a single vehicle.',
    },
  ];

  const openFundTypeModal = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsFundTypeModalOpen(true);
  };

  const closeFundTypeModal = () => {
    setIsFundTypeModalOpen(false);
  };

  const fundTypeTooltipContent = (
    <Typography component="span" sx={{ fontSize: '13px', lineHeight: '18px' }}>
      <a
        href="#"
        onClick={openFundTypeModal}
        style={{
          color: '#7b569b',
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        Click here
      </a>
      {' '}for more details on our fund types.
    </Typography>
  );

  const SmallTooltip = ({ title }) => {
    const [open, setOpen] = useState(false);

    const handleScroll = () => {
      setOpen(false);
    };

    const handleOpen = (shouldOpen) => {
      if (shouldOpen) {
        setOpen(true);
        window.addEventListener('scroll', handleScroll, { once: true });
      } else {
        setOpen(false);
      }
    };

    useEffect(() => {
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }, []);

    return (
      <MuiTooltip
        title={title}
        placement="top-start"
        open={open}
        onOpen={() => handleOpen(true)}
        onClose={() => handleOpen(false)}
        leaveTouchDelay={0}
        disableTouchListener
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: '#f5f5f5',
              color: '#191919',
              fontSize: '13px',
              padding: '12px 16px',
              maxWidth: '400px',
              border: '1px solid #e5e5e5',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            },
          },
        }}
      >
        <Box
          component="span"
          sx={{
            ml: 0.5,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#999',
            '&:hover': {
              color: '#7b569b',
            },
          }}
          onClick={() => handleOpen(true)}
        >
          <InfoOutlinedIcon fontSize="small" />
        </Box>
      </MuiTooltip>
    );
  };

  const selectStyles = {
    minWidth: 180,
    '& .MuiInputLabel-root': {
      display: 'none',
      height: 0,
      margin: 0,
      padding: 0,
      minHeight: 0,
    },
    '& .MuiFormControl-root': {
      marginTop: 0,
    },
    '& .MuiSelect-icon, & .MuiSvgIcon-root': {
      color: '#191919',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e0e0e0',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#191919',
      borderWidth: 1,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#191919',
    },
  };

  if (isLoading) {
    return (
      <FundsTableContainer>
        <FundsTableHeader>
          <FundsTableTitle component="h2">Available Funds & Deadlines</FundsTableTitle>
        </FundsTableHeader>
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: '200px' }}>
          <Loader />
        </Box>
      </FundsTableContainer>
    );
  }

  if (isError) {
    return (
      <FundsTableContainer>
        <FundsTableHeader>
          <FundsTableTitle component="h2">Available Funds & Deadlines</FundsTableTitle>
        </FundsTableHeader>
        <Box sx={{ p: 3, textAlign: 'center', color: '#666' }}>
          Error loading funds data. Please try again later.
        </Box>
      </FundsTableContainer>
    );
  }

  return (
    <FundsTableContainer>
      <FundsTableHeader>
        <FundsTableTitle component="h2">Available Funds & Deadlines</FundsTableTitle>
      </FundsTableHeader>

      <FiltersContainer>
        <FilterWrapper>
          <FilterLabel>Fund Type</FilterLabel>
          <StyledSelect
            label=""
            options={fundTypeOptions}
            value={fundTypeFilter}
            onChange={handleFundTypeChange}
            sx={selectStyles}
          />
        </FilterWrapper>
        <FilterWrapper>
          <FilterLabel>Fund Family</FilterLabel>
          <StyledSelect
            label=""
            options={fundFamilyOptions}
            value={fundFamilyFilter}
            onChange={handleFundFamilyChange}
            sx={selectStyles}
          />
        </FilterWrapper>
      </FiltersContainer>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableHeaderCell>FUND FAMILY</StyledTableHeaderCell>
              <StyledTableHeaderCell>FUND VINTAGE</StyledTableHeaderCell>
              <StyledTableHeaderCell>
                <Box display="flex" alignItems="center" gap={0.5}>
                  TYPE
                  <SmallTooltip title={fundTypeTooltipContent} />
                </Box>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell className="sortable" onClick={() => handleSort('fundOpens')}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  FUND OPENS
                  {getSortIcon('fundOpens')}
                </Box>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell className="sortable" onClick={() => handleSort('firstClose')}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  FIRST CLOSE
                  {getSortIcon('firstClose')}
                  <SmallTooltip title={tooltipContent} />
                </Box>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell className="sortable" onClick={() => handleSort('secondClose')}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  SECOND CLOSE
                  {getSortIcon('secondClose')}
                  <SmallTooltip title={tooltipContent} />
                </Box>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell className="sortable" onClick={() => handleSort('finalClose')}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  FINAL CLOSE
                  {getSortIcon('finalClose')}
                  <SmallTooltip title={tooltipContent} />
                </Box>
              </StyledTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) => {
              const fundType = row.type;
              const isEven = index % 2 === 0;
              const vintage = fundType === 'Foundation Fund'
                ? formatFoundationVintage(row.fundVintage) 
                : row.fundVintage;
              const isClickable = row.isOpen && !!row.qcpLink;

              return (
                <StyledTableRow key={`${row.fundFamilyName}-${row.fundVintage}-${index}`} isEven={isEven}>
                  <StyledTableCell className={isClickable ? 'purple-column' : ''}>
                    {isClickable ? (
                      <ClickableLink
                        component="span"
                        onClick={() => row.qcpLink && window.open(row.qcpLink, '_blank')}
                      >
                        {row.fundFamilyName}
                      </ClickableLink>
                    ) : (
                      row.fundFamilyName
                    )}
                  </StyledTableCell>
                  <StyledTableCell className={isClickable ? 'purple-column' : ''}>
                    {isClickable ? (
                      <ClickableLink
                        component="span"
                        onClick={() => row.qcpLink && window.open(row.qcpLink, '_blank')}
                      >
                        {vintage}
                      </ClickableLink>
                    ) : (
                      vintage
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    <FundTypePill fundType={fundType}>
                      {fundType}
                    </FundTypePill>
                  </StyledTableCell>
                  <StyledTableCell>{formatDateTable(row.fundOpens)}</StyledTableCell>
                  <StyledTableCell>{formatDateTable(row.firstClose)}</StyledTableCell>
                  <StyledTableCell>{formatDateTable(row.secondClose)}</StyledTableCell>
                  <StyledTableCell>{formatDateTable(row.finalClose)}</StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <MuiTablePagination
        component="div"
        count={filteredAndSortedData.length}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100]}
      />

      <Dialog
        open={isFundTypeModalOpen}
        onClose={closeFundTypeModal}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          {fundTypeModalParagraphs.map((paragraph) => (
            <Typography
              key={paragraph}
              sx={{
                fontSize: '15px',
                lineHeight: '24px',
                color: '#191919',
                mb: 2,
              }}
            >
              {paragraph}
            </Typography>
          ))}
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {fundTypeModalBullets.map((bullet) => (
              <Typography
                component="li"
                key={bullet.key}
                sx={{
                  fontSize: '15px',
                  lineHeight: '22px',
                  color: '#191919',
                  mb: 1,
                  pl: 0.25,
                }}
              >
                {bullet.title && <strong>{bullet.title}</strong>}
                {bullet.description}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeFundTypeModal}
            variant="outlined"
            sx={{
              textTransform: 'none',
              '&&': {
                fontWeight: 700,
                color: '#7b569b',
                borderColor: '#7b569b',
              },
              px: 1.5,
              py: 0.5,
              minWidth: 0,
              lineHeight: 1.2,
              '&&:hover': {
                borderColor: '#7b569b',
                backgroundColor: 'rgba(123, 86, 155, 0.06)',
              },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </FundsTableContainer>
  );
};

export default FundsTable;
